/** Only static categories survive. Regex removal alone cannot protect freeform input. */
const errorNames = new Set(['Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'URIError', 'EvalError'])
const errorCategories: ReadonlyArray<readonly [RegExp, string]> = [
  [/^Cannot read properties of undefined\b/, 'Cannot read properties of undefined'],
  [/^Cannot read properties of null\b/, 'Cannot read properties of null'],
  [/^Failed to fetch dynamically imported module\b/, 'Failed to load application module'],
  [/^Loading chunk [\w-]+ failed\b/, 'Failed to load application module'],
  [/^Maximum call stack size exceeded\b/, 'Maximum call stack size exceeded'],
  [/^Failed to fetch$/, 'Network request failed'],
]

export function sanitizeError(name: unknown, message: unknown) {
  return {
    name: typeof name === 'string' && errorNames.has(name) ? name : 'Error',
    message: typeof message === 'string'
      ? errorCategories.find(([pattern]) => pattern.test(message))?.[1] ?? 'Application error'
      : 'Application error',
  }
}

export interface RequestDiagnosticInput {
  method: string
  path_template: string
  status?: number | null
  failure_kind?: string
  duration_ms: number
  request_id?: string | null
}

export interface SafeRequestDiagnostic {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path_template: string
  status?: number
  failure_kind: 'http' | 'network' | 'timeout'
  duration_ms: number
  request_id?: string
}

export function sanitizeRequest(input: RequestDiagnosticInput, templates: ReadonlySet<string>): SafeRequestDiagnostic | null {
  const path = input.path_template
  if (!templates.has(path) || path.length > 160 || !/^\/api\/v1\/[A-Za-z0-9_/{}/.:-]+$/.test(path)) return null
  if (/\/(?:auth|problem-reports|credentials|tokens|secrets)(?:\/|$)/i.test(path)) return null
  const method = input.method.toUpperCase()
  if (!['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return null
  const failure = input.failure_kind ?? 'http'
  if (!['http', 'network', 'timeout'].includes(failure)) return null
  if (failure === 'http' && (!Number.isInteger(input.status) || input.status! < 400 || input.status! > 599)) return null
  if (!Number.isFinite(input.duration_ms) || input.duration_ms < 0) return null
  const safe: SafeRequestDiagnostic = {
    method: method as SafeRequestDiagnostic['method'], path_template: path,
    failure_kind: failure as SafeRequestDiagnostic['failure_kind'],
    duration_ms: Math.min(600_000, Math.round(input.duration_ms)),
  }
  if (failure === 'http') safe.status = input.status!
  if (typeof input.request_id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(input.request_id)) {
    safe.request_id = input.request_id
  }
  return safe
}
